// V4 Calm (2026-10-04, UX review docs/GUI_REDESIGN_AUDIT_2026_10_04.md + the review that followed it): V2's situational idea
// on V3's navigation. One answer first (how am I doing, does anything need me), then a single ranked list of what needs you
// (each fact once, one action each), then the positions that matter, then details only on request. Only real data: where the
// API does not expose something, the section is left out instead of showing a placeholder.
import React, { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import useDeskData from '../lib/useDeskData'
import { buildAttention, statusSentence } from '../lib/attention'
import { resolveLink } from '../lib/alertGroups'
import { mainRows, tier2Rows, EquityChart, OrdersList } from '../components/deskParts'
import { timeAgo } from '../lib/utils'
import '../styles/calm.css'

const money = (v, d = 0) => (v == null || Number.isNaN(Number(v)) ? '—' : `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: d, maximumFractionDigits: d })}`)
const pct = (v) => (v == null || Number.isNaN(Number(v)) ? '—' : `${Number(v) >= 0 ? '+' : '−'}${Math.abs(Number(v)).toFixed(2)}%`)
const sdollar = (v) => (v == null || Number.isNaN(Number(v)) ? '—' : `${Number(v) >= 0 ? '+' : '−'}$${Math.abs(Number(v)).toLocaleString('en-US', { maximumFractionDigits: 2 })}`)
const AGE = (s) => (s == null ? '' : s < 90 ? `${Math.round(s)}s ago` : s < 5400 ? `${Math.round(s / 60)}m ago` : `${(s / 3600).toFixed(1)}h ago`)

function Disclosure({ label, hint, open, onToggle, children }) {
  return (
    <div>
      <button className="calm-disc" aria-expanded={open} onClick={onToggle}>
        <span>{label}{hint && <span className="calm-cap" style={{ marginLeft: 10, fontWeight: 400 }}>{hint}</span>}</span>
        <span aria-hidden="true" style={{ color: 'var(--c-3)' }}>{open ? '▾' : '▸'}</span>
      </button>
      {open && <div style={{ padding: '4px 4px 8px' }}>{children}</div>}
    </div>
  )
}

export default function CalmHome() {
  const d = useDeskData({ pollMs: 30_000 })
  const [showAllAttention, setShowAllAttention] = useState(false)
  const [showAllPositions, setShowAllPositions] = useState(false)
  const [sysOpen, setSysOpen] = useState(false)
  const [jobsOpen, setJobsOpen] = useState(false)
  const [ordersOpen, setOrdersOpen] = useState(false)

  const att = useMemo(() => buildAttention({ health: d.health, ws: d.ws, alerts: d.alerts, mainSummary: d.main?.summary, schwab: d.schwab }),
    [d.health, d.ws, d.alerts, d.main, d.schwab])
  const isMain = d.selectedAccount === 'main'
  const acct = d.account
  const rows = useMemo(() => (isMain ? mainRows(d.main) : tier2Rows(d.tier2)), [isMain, d.main, d.tier2])
  const picked = useMemo(() => {
    if (showAllPositions) return [...rows].sort((a, b) => (a.has_stop === false ? 0 : 1) - (b.has_stop === false ? 0 : 1) || a.symbol.localeCompare(b.symbol))
    const unprot = rows.filter(r => r.has_stop === false)
    const movers = rows.filter(r => r.has_stop !== false).sort((a, b) => Math.abs(b.pnl_pct ?? 0) - Math.abs(a.pnl_pct ?? 0)).slice(0, Math.max(0, 8 - unprot.length))
    return [...unprot, ...movers]
  }, [rows, showAllPositions])

  const items = showAllAttention ? att.items : att.items.slice(0, 3)
  const hasAlertItems = att.items.some(i => i.ackIds)
  const summary = d.main?.summary
  const invested = isMain && summary ? summary.market_value : null
  const workers = Object.entries(d.health?.workers || {}).filter(([k]) => !/^(asyncio|ThreadPool)/.test(k))
  const sysIssues = att.items.filter(i => !i.ackIds && i.key !== 'unprotected' && i.key !== 'schwab').length
  const change = acct?.intraday_pl

  return (
    <div className="calm" data-testid="calm-home">
      {/* 1. The answer */}
      <section className="calm-card calm-hero" aria-label="Summary">
        <div>
          <div className="calm-hero-label">{isMain ? 'MAIN account' : 'TIER 2 account'} · paper trading</div>
          <div className="calm-hero-value num">{money(acct?.portfolio_value)}</div>
          <div className={`calm-hero-change num ${change == null ? '' : change >= 0 ? 'calm-up' : 'calm-down'}`}>
            {change == null ? '' : `${sdollar(change)} (${pct(acct?.intraday_pl_pct)}) today`}
          </div>
        </div>
        <div className={`calm-pill ${att.status}`} role="status">
          <span className="calm-dot" />{statusSentence(att)}
        </div>
      </section>

      {/* 2. What needs you: one ranked list, each fact once */}
      <section className="calm-card" aria-label="Needs you">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 180 }}>
            <h2 className="calm-title">{att.items.length ? 'Needs you' : 'Nothing needs you'}</h2>
            {att.items.length === 0 && <p className="calm-sub">Every position has a stop-loss, the engine is running and no alerts are open.</p>}
          </div>
          {hasAlertItems && (
            <a className="calm-btn primary" href={resolveLink()}
              title="Opens Claude Code on this machine with the fix prompt typed in. Nothing is sent until you press Enter.">
              Fix with Claude
            </a>
          )}
        </div>
        {items.map(i => (
          <div className="calm-item" key={i.key}>
            <span className={`calm-sev ${i.sev}`} aria-label={i.sev === 'critical' ? 'urgent' : 'to review'} />
            <div style={{ minWidth: 0 }}>
              <div className="calm-item-title">{i.title}{i.count > 1 ? <span className="calm-cap" style={{ marginLeft: 8, fontWeight: 400 }}>×{i.count}{i.latest ? ` · latest ${timeAgo(i.latest)}` : ''}</span> : null}</div>
              <div className="calm-item-why">{i.why}</div>
            </div>
            {i.href && <Link className="calm-btn" to={i.href}>{i.action}</Link>}
            {i.ackIds && <button className="calm-btn" onClick={() => d.ackGroup(i.ackIds)}>{i.ackIds.length > 1 ? `Dismiss ${i.ackIds.length}` : 'Dismiss'}</button>}
          </div>
        ))}
        {att.items.length > 3 && (
          <button className="calm-btn" style={{ marginTop: 8 }} onClick={() => setShowAllAttention(v => !v)}>
            {showAllAttention ? 'Show fewer' : `Show ${att.items.length - 3} more`}
          </button>
        )}
      </section>

      <div className="calm-cols">
        {/* 3. The positions that matter */}
        <section className="calm-card" aria-label="Positions">
          <h2 className="calm-title">Your positions <span className="calm-cap" style={{ fontWeight: 400 }}>{isMain ? 'MAIN' : 'TIER 2'}</span></h2>
          <p className="calm-sub">
            {rows.length === 0 ? (d.main || d.tier2 ? 'No open positions.' : 'Loading…')
              : isMain && summary ? `${summary.count} open · ${summary.count - summary.unprotected_count} with a stop-loss${summary.unprotected_count ? ` · ${summary.unprotected_count} without` : ''}`
                : `${rows.length} open`}
          </p>
          <div style={{ marginTop: 8 }}>
            {picked.map(r => (
              <Link className="calm-rowlink" key={`${r.book}${r.symbol}`} to={isMain ? `/positions?s=${r.symbol}` : '/portfolio'}>
                <div style={{ minWidth: 0 }}>
                  <div><span className="calm-sym">{r.symbol}</span>{r.has_stop === false && <span className="calm-badge">No stop-loss</span>}</div>
                  <div className="calm-cap num">{r.qty == null ? '' : `${Number(r.qty).toLocaleString('en-US', { maximumFractionDigits: 0 })} shares`}{r.value != null ? ` · ${money(r.value)}` : ''}</div>
                </div>
                <div className="calm-pl num">
                  <div className={`calm-pl-main ${(r.pnl_pct ?? 0) >= 0 ? 'calm-up' : 'calm-down'}`}>{pct(r.pnl_pct)}</div>
                  <div className="calm-pl-sub">{r.pnl == null ? '' : sdollar(r.pnl)}</div>
                </div>
              </Link>
            ))}
          </div>
          {rows.length > picked.length || showAllPositions ? (
            <button className="calm-btn" style={{ marginTop: 8 }} onClick={() => setShowAllPositions(v => !v)}>
              {showAllPositions ? 'Show fewer' : `Show all ${rows.length}`}
            </button>
          ) : null}
          {!showAllPositions && rows.length > 0 && <p className="calm-cap" style={{ marginTop: 8 }}>Showing positions without a stop-loss first, then the biggest movers.</p>}
        </section>

        {/* 4. Your money */}
        <section className="calm-card" aria-label="Your money" style={{ alignSelf: 'start' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <h2 className="calm-title" style={{ flex: 1 }}>Your money</h2>
            <div className="calm-seg" role="group" aria-label="Period">
              {['1D', '1W', '1M'].map(p => <button key={p} aria-pressed={d.period === p} onClick={() => d.setPeriod(p)}>{p}</button>)}
            </div>
          </div>
          <div style={{ margin: '12px 0' }}><EquityChart data={d.equity} height={170} fontSize={12} /></div>
          <dl className="calm-kv num">
            <dt>Cash to trade</dt><dd>{money(acct?.buying_power)}</dd>
            {invested != null && (<><dt>Invested</dt><dd>{money(invested)}{acct?.portfolio_value ? ` · ${Math.round((invested / acct.portfolio_value) * 100)}%` : ''}</dd></>)}
            {acct && (<><dt>Day trades used</dt><dd>{acct.pattern_day_trader ? acct.daytrade_count : `${acct.daytrade_count} of 3`}</dd></>)}
          </dl>
        </section>
      </div>

      {/* 5. Details on request */}
      <section className="calm-card" aria-label="Details" style={{ padding: '8px 16px' }}>
        <Disclosure label="System" hint={sysIssues ? `${sysIssues} to look at` : 'running normally'} open={sysOpen} onToggle={() => setSysOpen(v => !v)}>
          <dl className="calm-kv">
            <dt>Trading engine</dt><dd>{d.health?.scheduler_alive && d.health?.watchdog_alive ? 'running' : d.health ? 'problem' : '—'}</dd>
            <dt>Live prices</dt><dd>{d.ws ? `${d.ws.status === 'IDLE' || d.ws.status === 'CLOSED' ? 'idle (market closed)' : String(d.ws.status).toLowerCase()}${d.ws.seconds_since_tick != null ? ` · last tick ${AGE(d.ws.seconds_since_tick)}` : ''}` : '—'}</dd>
            <dt>Latest price bar</dt><dd>{d.health?.latest_data ? timeAgo(d.health.latest_data) : '—'}</dd>
            <dt>Market</dt><dd>{d.health?.market_session ?? '—'}</dd>
          </dl>
          <Disclosure label="Background jobs" hint={`${workers.length}`} open={jobsOpen} onToggle={() => setJobsOpen(v => !v)}>
            <table className="calm-jobs">
              <tbody>
                {workers.map(([k, v]) => (
                  <tr key={k}>
                    <td>{k}</td>
                    <td style={{ textAlign: 'right', color: v.last_ok === false ? 'var(--c-red)' : 'var(--c-2)' }}>{v.last_ok === false ? 'failed' : v.last_ok ? 'ok' : v.running ? 'running' : '—'}</td>
                    <td style={{ textAlign: 'right', color: 'var(--c-3)' }}>{AGE(v.last_finish_age_s)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Disclosure>
        </Disclosure>
        <div style={{ borderTop: '1px solid var(--c-line)' }}>
          <Disclosure label="Recent orders" hint={`${Math.min(5, d.orders.length)} latest`} open={ordersOpen} onToggle={() => setOrdersOpen(v => !v)}>
            <OrdersList orders={d.orders.slice(0, 5)} />
          </Disclosure>
        </div>
      </section>

      <p className="calm-cap" style={{ textAlign: 'center' }}>
        {d.asOf ? `Updated ${timeAgo(d.asOf)} · refreshes every 30 seconds` : 'Loading…'}
        {d.errors.length > 0 ? ` · could not refresh: ${d.errors.join(', ')}` : ''}
      </p>
    </div>
  )
}
